import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, AlertTriangle } from "lucide-react";

let geoCache = null;

// Liste mondiale des pays (base unique côté backend : /api/geo/countries)
export function useGeoCountries() {
  const [list, setList] = useState(geoCache || []);
  useEffect(() => {
    if (geoCache) return;
    api.get("/geo/countries").then(({ data }) => { geoCache = data.countries || []; setList(geoCache); }).catch(() => {});
  }, []);
  return list;
}

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Recherche de pays pour « Autre pays » — réutilise la base pays backend
export const CountrySearchDialog = ({ open, onOpenChange, onSelect }) => {
  const { t, lang } = useI18n();
  const countries = useGeoCountries();
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const n = norm(q.trim());
    if (!n) return countries;
    return countries.filter((c) => norm(c.name).includes(n) || norm(c.name_en).includes(n));
  }, [q, countries]);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQ(""); }}>
      <DialogContent data-testid="country-search-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>{t("geo.searchCountry")}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input data-testid="country-search-input" autoFocus className="border-0 focus-visible:ring-0 rounded-none"
            placeholder={t("geo.searchCountry")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border" data-testid="country-search-results">
          {filtered.map((c) => (
            <button key={c.name} data-testid={`country-option-${c.name}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between"
              onClick={() => { onSelect(c); onOpenChange(false); setQ(""); }}>
              <span>{lang === "en" ? c.name_en : c.name}</span>
              <span className="text-xs text-muted-foreground">{lang === "en" ? c.name : c.name_en}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground" data-testid="country-search-empty">{t("geo.noResult")}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Avertissement douanier automatique (Hub UK + pays d'origine != UK).
// L'adresse UK n'est révélée qu'après « J'ai compris » + Continuer.
export const CustomsWarningCard = ({ onConfirm }) => {
  const { t } = useI18n();
  const [ack, setAck] = useState(false);
  return (
    <div className="mt-3 border-2 border-amber-400 bg-amber-50 p-5 max-w-xl" data-testid="customs-warning-card">
      <div className="flex items-center gap-2 font-heading font-bold text-amber-900">
        <AlertTriangle className="h-5 w-5 text-amber-600" /> {t("customs.title")}
      </div>
      <div className="mt-3 space-y-2 text-sm text-amber-900">
        <p>{t("customs.p1")}</p>
        <p>{t("customs.p2")}</p>
        <p>{t("customs.p3")}</p>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-amber-900 cursor-pointer">
        <Checkbox data-testid="customs-ack-checkbox" checked={ack} onCheckedChange={(v) => setAck(!!v)} />
        {t("customs.ack")}
      </label>
      <Button data-testid="customs-continue-btn" disabled={!ack} onClick={onConfirm}
        className="mt-3 rounded-sm bg-amber-600 hover:bg-amber-700 text-white">
        {t("customs.continue")}
      </Button>
    </div>
  );
};
